import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { useTheme } from '@emotion/react'

import { InfoIcon } from '@segment/matcha/components/Icons/Common'

import Typography from '@segment/matcha/components/Typography'
import Divider from '@segment/matcha/components/Divider'
import Paper from '@segment/matcha/components/Paper'
import Tooltip from '@segment/matcha/components/Tooltip'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { usePageConfig } from '@segment/contexts/page'

import getCards from './data'

const PlansCards: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  const theme = useTheme()

  return (
    <Wrapper>
      <StyledSectionBase variant="centered">
        <Section.Contents>
          <CardColumns>
            {getCards(pageConfig).map((card, index) => {
              return (
                <StyledPaper key={index} elevation={3}>
                  <StyledTitle tag="h2" variant="h3">
                    {card.title}
                  </StyledTitle>
                  <div>
                    {card.excerpt.map((text) => (
                      <Typography key={text} variant="pMedium">
                        {text}
                      </Typography>
                    ))}
                  </div>
                  {card.button}
                  <Divider />
                  <ListHeading variant="pMedium">{card.benefitsHeading}</ListHeading>
                  {card.benefits.map((benefit) => (
                    <div key={benefit.title}>
                      <ListSubtitle variant="pMedium">{benefit.title}</ListSubtitle>
                      <List>
                        {benefit.list.map((item, index) => (
                          <StyledListItem key={`benefit-${index}`}>
                            <Benefit variant="pMedium">
                              {item.text}

                              {item.tooltip && (
                                <Tooltip label={item.tooltip} placement="top">
                                  <InfoIcon color={theme.palette.twilio.gray[40]} />
                                </Tooltip>
                              )}
                            </Benefit>
                          </StyledListItem>
                        ))}
                      </List>
                    </div>
                  ))}
                </StyledPaper>
              )
            })}
          </CardColumns>
        </Section.Contents>
      </StyledSectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  background-color: ${({ theme }) => theme.palette.twilio.mint[10]};
  padding-bottom: 40px;
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1280px;

  ${Section.Wrapper} {
    padding-top: 0;

    ${({ theme }) => theme.breakpoints.down(`md`)} {
      row-gap: 40px;
    }
  }
`

const CardColumns = styled.div`
  display: grid;
  gap: 32px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${({ theme }) => theme.breakpoints.up(`xl`)} {
    grid-template-columns: 416px auto;
  }
`

const StyledPaper = styled(Paper)`
  display: grid;
  background-color: ${({ theme }) => theme.palette.neutrals.white[100]};
  padding: 0px 24px 24px;
  grid-template-rows: repeat(6, min-content) 1fr;
  justify-self: center;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    padding: 8px 32px 10px 32px;
  }
`

const StyledTitle = styled(Typography)`
  margin-bottom: 0;
`

const ListSubtitle = styled(Typography)`
  margin: 0;
`

const ListHeading = styled(Typography)`
  font-weight: 600;
`

const List = styled.ul`
  margin-top: 8px;
  margin-bottom: 24px;
  padding-left: 24px;

  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    margin-bottom: 18px;
  }
`

const StyledListItem = styled.li`
  list-style-image: url("data:image/svg+xml;charset=UTF-8,%3csvg width='12' height='9' fill='none' xmlns='http://www.w3.org/2000/svg'%3e%3cpath fill-rule='evenodd' clip-rule='evenodd' d='M11.045.667a.91.91 0 0 1 .025 1.305L5.031 8.126a.95.95 0 0 1-1.356 0L.93 5.33a.91.91 0 0 1 .025-1.305.954.954 0 0 1 1.331.025l2.067 2.106L9.714.692a.954.954 0 0 1 1.33-.025Z' fill='%23121C2D'/%3e%3c/svg%3e ");

  font-weight: 400;
  padding: 0 0 8px 6px;
`

const Benefit = styled(Typography)`
  margin: 0;
  display: flex;
  gap: 8px;
`
export default PlansCards
